import axios, { retryOnErrors, throttleRequests } from '@fs/zion-axios'
import zionDebug from '@fs/zion-debug'
import { makeUseAxios } from 'axios-hooks'

const debug = zionDebug('temple:axios')

const MIN_THROTTLING_SECS = 50

/** An axios instance that retries on 429 (and several other) errors. */
export const axiosRetry = (() => {
  const axiosInstance = axios.create({ useNewInterceptors: true })
  retryOnErrors(axiosInstance)
  return axiosInstance
})()

export default axiosRetry

export const useZionAxiosRetry = makeUseAxios({
  axios: axiosRetry,
})

/**
 * Calls axios.get() on an array of URLs, throttling to issue no more than `callsPerSec`
 * requests per second. Uses Promise.allSettled() to ensure partial failures don't lose all data.
 * @param {string[]} urls - The URLs to call.
 * @param {number} callsPerSec - The number of calls per second to throttle to. 0 for no throttling.
 * @returns  {Promise<Array>} - Promise of successful url request results (failed requests are logged but not returned)
 */
export async function axiosThrottled(urls, callsPerSec = 0) {
  let axiosInstance = axiosRetry
  if (callsPerSec && callsPerSec * MIN_THROTTLING_SECS <= urls.length) {
    axiosInstance = axios.create({ useNewInterceptors: true })
    retryOnErrors(axiosInstance)
    throttleRequests(axiosInstance, 1000 / callsPerSec)
    debug(`Throttling requests at ${callsPerSec} calls per second`)
  }

  const urlRequests = urls.map((url) => axiosInstance.get(url))
  const results = await Promise.allSettled(urlRequests)

  // Filter for successful results and log failures
  return results.reduce((successfulResults, result, index) => {
    if (result.status === 'fulfilled') {
      successfulResults.push(result.value)
    } else {
      console.error(`axiosThrottled: Request to ${urls[index]} failed:`, result.reason)
    }
    return successfulResults
  }, [])
}
